import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import CaixaPortsTable from '../caixa/CaixaPortsTable';
import { loadCaixaPortsData } from '../../utils/loadCaixaPorts';
import { CaixaPort } from '../../types/caixaPort';
import { FtthCaixa } from '../../services/ftthCaixaService';

interface CaixaPortsListModalProps {
  open: boolean;
  onClose: () => void;
  caixaId?: number;
  caixaName?: string;
}

const CaixaPortsListModal: React.FC<CaixaPortsListModalProps> = ({
  open,
  onClose,
  caixaId,
  caixaName
}) => {
  const [caixa, setCaixa] = useState<FtthCaixa | null>(null);
  const [ports, setPorts] = useState<CaixaPort[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPorts = useCallback(async () => {
    if (!caixaId) return;

    setLoading(true);
    try {
      const data = await loadCaixaPortsData(caixaId);
      setCaixa(data.caixa);
      setPorts(data.ports);
    } catch {
      toast.error('Erro ao carregar portas da caixa');
      setCaixa(null);
      setPorts([]);
    } finally {
      setLoading(false);
    }
  }, [caixaId]);

  useEffect(() => {
    if (open && caixaId) {
      fetchPorts();
    }
  }, [open, caixaId, fetchPorts]);

  const handleClose = () => {
    setCaixa(null);
    setPorts([]);
    onClose();
  };

  const displayName = caixaName || caixa?.name || (caixaId ? `Caixa #${caixaId}` : 'Caixa');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Portas — {displayName}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton onClick={fetchPorts} disabled={loading} size="small" title="Atualizar portas">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5 }}>
        <CaixaPortsTable
          ports={ports}
          loading={loading}
          capacidade={caixa?.capacidade}
          showTitle
        />
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} color="primary" size="small">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaixaPortsListModal;
